/**
 * Splitting a step's sentence into the thing it is called and the thing to do.
 *
 * The approved copy already carries the division — "Préparer - Lavez-vous les
 * mains…", "Inject - Pinch the skin…" — as a dash, which a card rendered as one
 * run of bold text. So every step read as a wall, and the word that says where
 * she is in the procedure was buried in the middle of the instruction.
 *
 * Pulling it apart here rather than rewriting the content keeps the two in
 * step: the words are unchanged, the reviewed sentence is intact, and nothing
 * new is said. It is the same separator `speakableStepText` turns into a pause,
 * for the same reason — it was always a sentence break doing a dash's job.
 *
 * **Only the first one.** The copy uses the dash again inside the instruction
 * ("Ovitrelle est prérempli - aucune préparation…"), and splitting on that too
 * would promote half an instruction to a heading.
 */

export type StepCopy = {
  /** The step's name, or null where the copy carries no separator. */
  title: string | null;
  /** Everything else. Never empty — a split that would empty it is refused. */
  body: string;
};

/** Spaced hyphen, en dash or em dash, whichever the locale's copy happens to use. */
const SEPARATOR = /\s[-–—]\s/u;

export function splitStepCopy(text: string): StepCopy {
  const trimmed = text.trim();
  const match = SEPARATOR.exec(trimmed);
  if (!match || match.index === undefined) {
    return { title: null, body: trimmed };
  }

  const title = trimmed.slice(0, match.index).trim();
  const body = trimmed.slice(match.index + match[0].length).trim();

  // A heading with nothing under it, or a heading longer than the instruction
  // it introduces, is not a heading. Both mean the dash was doing something
  // other than naming the step, so the sentence is left whole.
  if (!title || !body) return { title: null, body: trimmed };
  if (title.length > body.length) return { title: null, body: trimmed };

  return { title, body };
}
